import React from 'react';
import { Box } from '@mui/material';
import { Typography } from '@mui/joy';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { ChevronRightRounded } from '@mui-symbols-material/w300';
import { ClientIcon, getInstalledBaseTypeIcon, WidgetBox } from '@dreebit-registry/vsm-microservice-components';
import { SkeletonListRows, fadeInSx } from './HeroSkeletons';
import { HeroCardHeader, HERO_CARD_ICON_SIZE } from './heroPrimitives';
import { AssetCustomer } from '../../graphql/queries/assetCustomer';
import { SystemCustomerInfo } from '../../graphql/queries/systemCustomerInfo';
import useOwnClient from '../../hooks/useOwnClient';
import HeroCustomerContacts from './HeroCustomerContacts';
import { heroSectionSx } from './heroCardStyles';
import HeroEntity from './HeroEntity';

interface Props {
    variant: 'asset' | 'system';
    /** assetLocationId for the asset variant, systemId for the system variant. */
    id: string;
    /** Optional location path (semiconductor assets), e.g. Tool > Chamber, derived from pathNodes. */
    locationPath?: Array<{ title: string; nodeTypeKey: string }>;
    /** Installed base node of the asset/system — source of the customer contacts shown to internal viewers. */
    nodeId?: string;
}

const buildAddressLine = (address: any): string => {
    const zip = (_.get(address, 'postalCode', '') || '').trim();
    const city = (_.get(address, 'city', '') || '').trim();
    const country = (_.get(address, 'country.name', '') || '').trim();
    const left = [zip, city].filter(Boolean).join(' ');
    if (left && country) return `${left}, ${country}`;
    return left || country || _.get(address, 'title', '') || '';
};

/**
 * Customer card: company + address, with an optional Tool > Chamber location row for semiconductor.
 * Internal viewers (own company is a service provider) also get the customer's contact persons —
 * whom to call at the customer (IBM-1086). Customers know their own people, so they don't.
 */
const HeroCustomerCard = ({ variant, id, locationPath, nodeId }: Props) => {
    const { t } = useTranslation();
    const ownClient = useOwnClient();
    const showContacts = !!nodeId && ownClient?.isServiceProvider === true;

    const { data, loading } = useQuery(variant === 'asset' ? AssetCustomer : SystemCustomerInfo, {
        variables: { id },
        skip: !id,
    });

    const customer = variant === 'asset' ? _.get(data, 'assetLocation.owner') : _.get(data, 'system.client');
    const name = _.get(customer, 'name');
    const addressLine = customer ? buildAddressLine(_.get(customer, 'address')) : '';

    return (
        <WidgetBox>
            <HeroCardHeader title={t('Customer')} icon={<ClientIcon size={HERO_CARD_ICON_SIZE} color="#333" />} />
            {loading && !customer ? (
                <Box sx={{ pt: '6px' }}>
                    <SkeletonListRows variant="text" />
                </Box>
            ) : !customer ? (
                <Typography level="bodySmall" sx={{ color: '#777', pt: '12px' }}>
                    {t('No customer information')}
                </Typography>
            ) : (
                <Box sx={fadeInSx}>
                    <HeroEntity name={name} subline={addressLine} />
                    {locationPath && locationPath.length > 0 && (
                        <Box sx={{ ...heroSectionSx, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                            {locationPath.map((node, i) => (
                                <React.Fragment key={node.nodeTypeKey}>
                                    {i > 0 && <ChevronRightRounded sx={{ fontSize: 16, color: '#999' }} />}
                                    {getInstalledBaseTypeIcon(node.nodeTypeKey, 18, '#666')}
                                    <Typography level="bodySmall" sx={{ color: '#555' }}>
                                        {node.title}
                                    </Typography>
                                </React.Fragment>
                            ))}
                        </Box>
                    )}
                    {showContacts && <HeroCustomerContacts nodeId={nodeId!} />}
                </Box>
            )}
        </WidgetBox>
    );
};

export default HeroCustomerCard;
